import { useEffect, useMemo, useState } from "react";
import { pitchLabel } from "@/lib/pitchLabel";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { format, parseISO } from "date-fns";
import { Bell, BellOff, X, Plus } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { ProposeRefereeDialog } from "@/components/proposals/ProposeRefereeDialog";
import { ProposeRefereeChangeDialog } from "@/components/proposals/ProposeRefereeChangeDialog";
import { RefAvailabilityGrid } from "@/components/referees/RefAvailabilityGrid";

const REFEREE_AGE_ORDER = ["U7","U8","U9","U10","U11","U12","U13","U14","U15","U16","U18","Adults"];

export default function Referees() {
  const { user } = useAuth();
  const [referees, setReferees] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRefId, setSelectedRefId] = useState("");
  const [refBookings, setRefBookings] = useState(null);
  const [refLoading, setRefLoading] = useState(false);
  const [filterAge, setFilterAge] = useState("all");
  const [filterDay, setFilterDay] = useState("all");
  const [proposeOpen, setProposeOpen] = useState(false);
  const [changeRef, setChangeRef] = useState(null);
  const isAdmin = user?.role === "admin";

  useEffect(() => { (async () => {
    const [r, l] = await Promise.all([api.get("/referees"), api.get("/leagues")]);
    setReferees(r.data); setLeagues(l.data);
    setLoading(false);
  })(); }, []);

  useEffect(() => {
    if (!selectedRefId) { setRefBookings(null); return; }
    setRefLoading(true);
    api.get(`/referees/${selectedRefId}/bookings`)
      .then(r => setRefBookings(r.data))
      .finally(()=>setRefLoading(false));
  }, [selectedRefId]);

  // Build unique (leagueName, age_group) columns from all league payment_grids
  // Plus a "Default" column for every league that has default_qualified_fee or default_unqualified_fee set.
  const columns = useMemo(() => {
    const set = new Map();
    leagues.forEach(l => {
      (l.payment_grid || []).forEach(row => {
        const key = `${l.id}::${row.age_group}`;
        if (!set.has(key)) set.set(key, { leagueId: l.id, leagueName: l.name, age: row.age_group, isDefault: false });
      });
      if (l.default_qualified_fee != null || l.default_unqualified_fee != null) {
        const key = `${l.id}::__default__`;
        if (!set.has(key)) set.set(key, { leagueId: l.id, leagueName: l.name, age: "Default", isDefault: true });
      }
    });
    return Array.from(set.values()).sort((a,b) => {
      if (a.leagueName !== b.leagueName) return a.leagueName.localeCompare(b.leagueName);
      // Within a league, keep Default last
      if (a.isDefault && !b.isDefault) return 1;
      if (!a.isDefault && b.isDefault) return -1;
      return a.age.localeCompare(b.age);
    });
  }, [leagues]);

  const feeOf = (referee, col) => {
    const l = leagues.find(x => x.id === col.leagueId);
    if (!l) return null;
    const leagueDefault = referee.is_qualified ? l.default_qualified_fee : l.default_unqualified_fee;
    if (col.isDefault) return leagueDefault ?? null;
    const row = (l.payment_grid || []).find(r => (r.age_group || "").toLowerCase() === (col.age || "").toLowerCase());
    if (!row) return leagueDefault ?? null;
    const specific = referee.is_qualified ? row.qualified_fee : row.unqualified_fee;
    // Grid row present but this qualified/unqualified column is blank → fall back to league default
    if (specific == null || specific === "") return leagueDefault ?? null;
    return specific;
  };

  // Group columns by league for nice header spans
  const groupedCols = useMemo(() => {
    const groups = [];
    columns.forEach(c => {
      const last = groups[groups.length - 1];
      if (last && last.leagueId === c.leagueId) last.cols.push(c);
      else groups.push({ leagueId: c.leagueId, leagueName: c.leagueName, cols: [c] });
    });
    return groups;
  }, [columns]);

  // Filtered referees — applied to both the card grid and the fee matrix rows.
  const filteredReferees = useMemo(() => referees.filter(r => {
    if (filterAge !== "all") {
      // Referee must be able to officiate this age. No max_age_group set = any age.
      if (r.max_age_group) {
        const maxIdx = REFEREE_AGE_ORDER.indexOf(r.max_age_group);
        const wantIdx = REFEREE_AGE_ORDER.indexOf(filterAge);
        if (maxIdx !== -1 && wantIdx !== -1 && wantIdx > maxIdx) return false;
      }
    }
    if (filterDay === "saturday" && r.available_saturday === false) return false;
    if (filterDay === "sunday" && r.available_sunday === false) return false;
    if (filterDay === "both" && (r.available_saturday === false || r.available_sunday === false)) return false;
    return true;
  }), [referees, filterAge, filterDay]);
  const anyFilter = filterAge !== "all" || filterDay !== "all";
  const clearFilters = () => { setFilterAge("all"); setFilterDay("all"); };

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">Directory</div>
          <h1 className="font-display font-black text-4xl md:text-5xl mt-2">Referees</h1>
          <p className="opacity-60 mt-2 text-sm">{referees.length} active referee{referees.length !== 1 && "s"} · view contact, qualification, assignments and fee matrix. {isAdmin ? "" : "Admin only can edit."}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setProposeOpen(true)}
            data-testid="propose-referee-btn"
            className="px-5 py-2.5 rounded-md border border-[#83B68B]/50 text-[#83B68B] hover:bg-[#83B68B]/10 inline-flex items-center gap-2 text-xs tracking-widest uppercase font-display font-bold"
          >
            <Plus className="w-4 h-4" /> Propose new referee
          </button>
          {isAdmin && <Link to="/app/admin" className="btn-volt px-5 py-2.5 rounded-md text-sm">Edit in Admin</Link>}
        </div>
      </div>

      <RefAvailabilityGrid canAsk={isAdmin || user?.is_club_secretary} leagues={leagues} />
      <ProposeRefereeDialog open={proposeOpen} onClose={() => setProposeOpen(false)} />
      <ProposeRefereeChangeDialog referee={changeRef} onClose={() => setChangeRef(null)} />

      {loading ? (
        <div className="opacity-50 font-display tracking-widest">LOADING…</div>
      ) : referees.length === 0 ? (
        <div className="card-tactical rounded-md p-10 text-center opacity-60">No referees added yet.</div>
      ) : (
        <>
          <div className="mb-6 flex flex-wrap gap-3 items-center card-tactical rounded-md p-4" data-testid="referees-filter-bar">
            <div className="text-[10px] tracking-widest uppercase opacity-60 mr-1">Filter</div>
            <Select value={filterAge} onValueChange={setFilterAge}>
              <SelectTrigger className="w-44" data-testid="ref-filter-age"><SelectValue placeholder="Age group" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All age groups</SelectItem>
                {REFEREE_AGE_ORDER.map(a => <SelectItem key={a} value={a}>Can ref {a}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterDay} onValueChange={setFilterDay}>
              <SelectTrigger className="w-40" data-testid="ref-filter-day"><SelectValue placeholder="Match day" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any day</SelectItem>
                <SelectItem value="saturday">Saturday</SelectItem>
                <SelectItem value="sunday">Sunday</SelectItem>
                <SelectItem value="both">Both weekend days</SelectItem>
              </SelectContent>
            </Select>
            {anyFilter && (
              <button onClick={clearFilters} className="text-xs tracking-widest uppercase px-3 py-1.5 border border-border hover:border-[#83B68B] hover:text-[#83B68B] rounded inline-flex items-center gap-1.5" data-testid="ref-filter-clear">
                <X className="w-3 h-3" /> Clear
              </button>
            )}
            <div className="flex-1" />
            <div className="text-xs opacity-60" data-testid="ref-filter-count">{filteredReferees.length} of {referees.length}</div>
          </div>

          {filteredReferees.length === 0 ? (
            <div className="card-tactical rounded-md p-10 text-center opacity-60 mb-10">No referees match those filters.</div>
          ) : (
          <div className="grid md:grid-cols-2 gap-4 mb-10" data-testid="referees-list">
            {filteredReferees.map(r => (
              <div key={r.id} className="card-tactical rounded-md p-5" data-testid={`referee-view-${r.id}`}>
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                  <div className="font-display font-bold text-lg tracking-tight flex items-center gap-2 flex-wrap">
                    {r.name}
                    {r.is_qualified
                      ? <span className="text-[9px] tracking-widest uppercase bg-[#83B68B]/20 text-[#83B68B] px-1.5 py-0.5 rounded border border-[#83B68B]/30">Qualified</span>
                      : <span className="text-[9px] tracking-widest uppercase bg-muted opacity-70 px-1.5 py-0.5 rounded border border-border">Unqualified</span>}
                    {r.is_club_member && <span className="text-[9px] tracking-widest uppercase bg-[#83B68B]/10 text-[#83B68B] px-1.5 py-0.5 rounded border border-[#83B68B]/30" data-testid={`ref-club-member-${r.id}`}>Club member</span>}
                    {r.max_age_group && <span className="text-[9px] tracking-widest uppercase bg-muted px-1.5 py-0.5 rounded border border-border" data-testid={`ref-max-age-${r.id}`}>Up to {r.max_age_group}</span>}
                    <span className="text-[9px] tracking-widest uppercase bg-muted opacity-70 px-1.5 py-0.5 rounded border border-border" data-testid={`ref-days-${r.id}`}>
                      {r.available_saturday === false && r.available_sunday === false ? "No weekends"
                        : `${r.available_saturday !== false ? "Saturdays" : ""}${r.available_saturday !== false && r.available_sunday !== false ? " · " : ""}${r.available_sunday !== false ? "Sundays" : ""}`}
                    </span>
                    {r.notify_enabled ? <Bell className="w-3.5 h-3.5 text-[#83B68B]" title="Notifications on" /> : <BellOff className="w-3.5 h-3.5 opacity-40" title="Notifications off" />}
                  </div>
                  <button
                    type="button"
                    onClick={() => setChangeRef(r)}
                    data-testid={`ref-suggest-change-${r.id}`}
                    className="text-[10px] tracking-widest uppercase px-2 py-1 rounded border border-border hover:border-[#83B68B]/60 hover:text-[#83B68B] transition-colors"
                  >
                    Suggest changes
                  </button>
                </div>
                <div className="mt-2 text-xs opacity-70 space-y-0.5">
                  {r.level && <div>{r.level}</div>}
                  {r.email && <div>✉ <a href={`mailto:${r.email}`} data-testid={`ref-email-${r.id}`} className="hover:text-[#83B68B] hover:underline">{r.email}</a></div>}
                  {r.phone && <div>📞 <a href={`tel:${(r.phone || "").replace(/\s+/g, "")}`} data-testid={`ref-phone-${r.id}`} className="hover:text-[#83B68B] hover:underline">{r.phone}</a></div>}
                  {!r.email && !r.phone && !r.level && <div className="opacity-40">No contact details</div>}
                </div>
                {(r.parent_name || r.parent_phone || r.parent_email) && (
                  <div className="mt-3 border-t border-border pt-2 text-xs" data-testid={`ref-parent-${r.id}`}>
                    <div className="text-[10px] tracking-widest uppercase opacity-50 mb-1">Parent contact (U18)</div>
                    <div className="opacity-90 flex flex-wrap gap-x-3 gap-y-0.5">
                      {r.parent_name && <span className="font-medium">{r.parent_name}</span>}
                      {r.parent_phone && <a href={`tel:${(r.parent_phone || "").replace(/\s+/g, "")}`} className="opacity-70 hover:text-[#83B68B]">📞 {r.parent_phone}</a>}
                      {r.parent_email && <a href={`mailto:${r.parent_email}`} className="opacity-70 hover:text-[#83B68B]">✉ {r.parent_email}</a>}
                    </div>
                  </div>
                )}
                {r.updated_at && (
                  <div className="mt-3 text-[10px] opacity-40 tracking-widest uppercase" data-testid={`ref-audit-${r.id}`}>
                    Updated {format(parseISO(r.updated_at), "d MMM yyyy HH:mm")}{r.updated_by_name ? ` · ${r.updated_by_name}` : ""}
                  </div>
                )}
                {isAdmin && leagues.length > 0 && (
                  <div className="mt-3 border-t border-border pt-3" data-testid={`ref-leagues-${r.id}`}>
                    <div className="text-[10px] tracking-widest uppercase opacity-70 mb-2">
                      Eligible leagues <span className="opacity-50">· by match day</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {leagues.map(lg => {
                        const md = lg.match_day || "both";
                        const ok = md === "saturday" ? r.available_saturday !== false : md === "sunday" ? r.available_sunday !== false : (r.available_saturday !== false || r.available_sunday !== false);
                        return (
                          <span key={lg.id} data-testid={`ref-league-${r.id}-${lg.id}`} title={`${lg.name} plays ${md === "both" ? "Sat & Sun" : md}`}
                            className={`text-[10px] tracking-widest uppercase px-2 py-1 rounded border ${ok ? "bg-[#83B68B]/10 text-[#83B68B] border-[#83B68B]/30" : "border-border opacity-40 line-through"}`}>
                            {lg.name}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          )}

          {/* Fee matrix */}
          {columns.length > 0 ? (
            <div className="card-tactical rounded-md p-5 mb-10" data-testid="referee-fee-matrix">
              <div className="mb-4">
                <div className="font-display font-bold text-xl tracking-tight">Fee matrix</div>
                <p className="text-xs opacity-60 mt-1">Per-referee fee for each league / age group. Rate follows each referee's qualified vs unqualified flag.</p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs border-separate" style={{ borderSpacing: 0 }}>
                  <thead>
                    <tr className="text-[10px] tracking-widest uppercase opacity-70">
                      <th className="text-left px-3 py-2 sticky left-0 bg-[hsl(var(--card))]">Referee</th>
                      {groupedCols.map(g => (
                        <th key={g.leagueId} colSpan={g.cols.length} className="text-center px-3 py-2 border-b border-[#83B68B]/40 text-[#83B68B] truncate">{g.leagueName}</th>
                      ))}
                    </tr>
                    <tr className="text-[10px] tracking-widest uppercase opacity-60">
                      <th className="text-left px-3 py-1 sticky left-0 bg-[hsl(var(--card))]"></th>
                      {columns.map(c => (
                        <th key={`${c.leagueId}::${c.age}`} className="px-3 py-1 opacity-70">{c.age}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredReferees.map(r => (
                      <tr key={r.id} className="border-t border-border">
                        <td className="px-3 py-2 sticky left-0 bg-[hsl(var(--card))] font-medium whitespace-nowrap">
                          {r.name} <span className="text-[10px] opacity-50">{r.is_qualified ? "Q" : "U"}</span>
                        </td>
                        {columns.map(c => {
                          const fee = feeOf(r, c);
                          return (
                            <td key={`${r.id}::${c.leagueId}::${c.age}`} className="px-3 py-2 text-center" data-testid={`fee-${r.id}-${c.leagueId}-${c.age}`}>
                              {fee != null ? <span className="text-[#83B68B] font-display font-black">£{fee}</span> : <span className="opacity-30">—</span>}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-2 text-[10px] opacity-50">Q = qualified · U = unqualified. Age-group cells fall back to the league default if no specific rate is set. Empty cells mean no rate anywhere on that league.</div>
            </div>
          ) : (
            <div className="card-tactical rounded-md p-5 mb-10 text-sm opacity-70 italic">No league payment grids set yet — add rows in Admin → Leagues to see the fee matrix.</div>
          )}

          {/* Referee assignments viewer */}
          <div className="card-tactical rounded-md p-5" data-testid="referee-assignments-panel">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
              <div>
                <div className="font-display font-bold text-xl tracking-tight">Referee assignments</div>
                <p className="text-xs opacity-60 mt-1">Pick a referee to see their upcoming and past matches.</p>
              </div>
              <div className="w-full md:w-80">
                <Label className="text-[10px] tracking-widest uppercase opacity-70">Referee</Label>
                <Select value={selectedRefId} onValueChange={setSelectedRefId}>
                  <SelectTrigger data-testid="ref-assign-select" className="mt-1.5"><SelectValue placeholder="Choose a referee" /></SelectTrigger>
                  <SelectContent>
                    {referees.map(r => <SelectItem key={r.id} value={r.id}>{r.name}{r.is_qualified ? " · Qualified" : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {!selectedRefId ? (
              <div className="opacity-50 text-sm italic">Select a referee above to view their assignments.</div>
            ) : refLoading || !refBookings ? (
              <div className="opacity-50 font-display tracking-widest py-6">LOADING…</div>
            ) : (
              <div className="space-y-8">
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B] mb-2">Upcoming ({refBookings.upcoming.length})</div>
                  {refBookings.upcoming.length === 0 ? (
                    <div className="opacity-50 text-sm px-3 py-4 italic">No upcoming assignments.</div>
                  ) : (
                    <div className="rounded border border-border overflow-hidden">
                      <div className="grid grid-cols-12 gap-3 items-center px-3 py-2 text-[10px] tracking-widest uppercase opacity-60">
                        <div className="col-span-2">Date</div>
                        <div className="col-span-2">Time</div>
                        <div className="col-span-3">Pitch</div>
                        <div className="col-span-3">Fixture</div>
                        <div className="col-span-2 text-right">Fee</div>
                      </div>
                      {refBookings.upcoming.map(b => (
                        <div key={b.id} className="grid grid-cols-12 gap-3 items-center border-t border-border px-3 py-2 text-xs">
                          <div className="col-span-2 font-display font-bold">{format(parseISO(b.booking_date), "d MMM yyyy")}</div>
                          <div className="col-span-2 opacity-70">{String(b.start_hour).padStart(2,"0")}:{String(b.start_min || 0).padStart(2,"0")} — {String(b.end_hour).padStart(2,"0")}:{String(b.end_min || 0).padStart(2,"0")}</div>
                          <div className="col-span-3 truncate">{pitchLabel(b)}</div>
                          <div className="col-span-3 opacity-80 truncate">{b.team_name || "—"} vs {b.opponent || "TBC"} {b.match_type ? `· ${b.match_type}` : ""}</div>
                          <div className="col-span-2 text-right font-display font-black text-[#83B68B]">{b.expected_referee_fee != null ? `£${Number(b.expected_referee_fee).toFixed(2)}` : "—"}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase opacity-70 mb-2">Past ({refBookings.past.length})</div>
                  {refBookings.past.length === 0 ? (
                    <div className="opacity-50 text-sm px-3 py-4 italic">No past assignments.</div>
                  ) : (
                    <div className="rounded border border-border overflow-hidden opacity-90">
                      {refBookings.past.slice(0, 20).map(b => (
                        <div key={b.id} className="grid grid-cols-12 gap-3 items-center border-t border-border px-3 py-2 text-xs">
                          <div className="col-span-2 font-display font-bold">{format(parseISO(b.booking_date), "d MMM yyyy")}</div>
                          <div className="col-span-2 opacity-70">{String(b.start_hour).padStart(2,"0")}:{String(b.start_min || 0).padStart(2,"0")}</div>
                          <div className="col-span-3 truncate">{pitchLabel(b)}</div>
                          <div className="col-span-3 opacity-80 truncate">{b.team_name || "—"} vs {b.opponent || "TBC"}</div>
                          <div className="col-span-2 text-right opacity-80">{b.expected_referee_fee != null ? `£${Number(b.expected_referee_fee).toFixed(2)}` : "—"}</div>
                        </div>
                      ))}
                      {refBookings.past.length > 20 && <div className="px-3 py-2 text-[10px] uppercase tracking-widest opacity-50 border-t border-border">Showing latest 20 of {refBookings.past.length}</div>}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
